import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useMediaQuery, useTheme } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { loadOrgUsers } from '../../../action/org/load-org-users';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../../employees/actions/load-org-employees-by-status';
import { loadSurveys } from '../../../employees/actions/survey/load-surveys';
import {
  employeesForOrgIdSelector,
  employeesStateForOrgIdAndStatusSelector,
} from '../../../reducers/employee-selectors';
import { surveysForOrgIdSelector } from '../../../reducers/survey-selectors';
import { usersForOrgIdSelector } from '../../../reducers/user-selectors';
import { createStartDateTriggeredCommunication } from '../../../start-date-triggered-communication/actions/create-start-date-triggered-communication';
import { loadStartDateTriggeredCommunications } from '../../../start-date-triggered-communication/actions/load-start-date-triggered-communications';
import { updateStartDateTriggeredCommunication } from '../../../start-date-triggered-communication/actions/update-start-date-triggered-communication';
import { startDateTriggeredCommunicationForOrgIdSelector } from '../../../start-date-triggered-communication/start-date-triggered-communication-selectors';
import { MultipleEmployeeUsersSelector } from '../../../widgets/multiple-employee-users-selector';

import { EmployeeGroupsBase } from '../../../employee-groups/components/employee-groups-base';
import { issueFilters } from '../../../employees/employee-list';
import { CreateSurveyDialog } from '../../../employees/surveys/create-survey-dialog';
import { FeatureFlagged, FeatureGatedVariant } from '../../../feature-gates/feature-flagged';
import { FeatureGates } from '../../../feature-gates/feature-gates';
import { AUTOMATION_TRIGGER_TYPES } from '../../../start-date-triggered-communication/automation-trigger-types';
import {
  offsetAndRecurrenceSame,
  sendingAnnually,
  sendOffsetType,
} from '../../../start-date-triggered-communication/start-date-triggered-communication-utils';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';
import { UiHideShowToggle } from '../../../ui/ui-hide-show-toggle';
import { UiSelect } from '../../../ui/ui-select';
import { UiSliderSelect } from '../../../ui/ui-slider-select';

import {
  DEFAULT_SDTA_SEND_TIME,
  DEFAULT_SDTC_SEND_TIME,
  getTimeZoneFromOffset,
  SDTC_TIME_ZONE_OPTIONS,
} from '../../../employees/utils/start-date-triggered-communication-time-utils';
import { CkEditorNewlineTrimmer } from '../../../util/ckeditor-newline-trimmer';
import { capitalize, humanize } from '../../../util/formatters';
import { LanguageCode, LANGUAGES } from '../../../util/languages';
import { MESSAGE_TYPE, MessageType } from '../../../util/message-types';
import { START_DATE_TRIGGERED_COMMUNICATION_TYPES } from '../../../util/start-date-triggered-communication-types';
import { VCardDialog } from '../../../widgets/contact-card/v-card-dialog';
import Message from '../../../widgets/legacy-message';
import { LoadingMessage } from '../../../widgets/loading-message';
import { LegacyMultiLanguageMessageComposer } from '../../../widgets/message-composer/legacy-multiple-language-message-composer';
import { MultiLanguageMessageComposer } from '../../../widgets/message-composer/multiple-language-message-composer';

const mapStateToProps = (state: any, { org }: InputProps) => ({
  employees: employeesForOrgIdSelector(state, org.org_id),
  employeesStateForOrgIdAndStatus: employeesStateForOrgIdAndStatusSelector(
    state,
    org.org_id,
    EMPLOYEE_STATUS.CURRENT,
  ),
  surveys: surveysForOrgIdSelector(state, org.org_id),
  startDateTriggeredCommunications: startDateTriggeredCommunicationForOrgIdSelector(
    state,
    org.org_id,
  ),
  users: usersForOrgIdSelector(state, org.org_id),
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>, { org }: InputProps) => ({
  actions: {
    loadOrgUsers: () => dispatch(loadOrgUsers(org.org_id)),
    loadSurveys: () => dispatch(loadSurveys(org.org_id)),
    loadEmployees: () => dispatch(loadOrgEmployeesByStatus(org.org_id, EMPLOYEE_STATUS.CURRENT)),
    loadStartDateTriggeredCommunications: () =>
      dispatch(loadStartDateTriggeredCommunications(org.org_id)),
    createStartDateTriggeredCommunication: (
      startDateTriggeredCommunication: StartDateTriggeredCommunicationCreateRequest,
    ) => dispatch(createStartDateTriggeredCommunication(startDateTriggeredCommunication)),
    updateStartDateTriggeredCommunication: (
      startDateTriggeredCommunicationId: number,
      update: StartDateTriggeredCommunicationUpdateRequest,
    ) =>
      dispatch(
        updateStartDateTriggeredCommunication(
          startDateTriggeredCommunicationId,
          update,
          org.org_id,
        ),
      ),
  },
});

type InputProps = {
  editingCommunication?: StartDateTriggeredCommunication | StartDateTriggeredCommunicationCopy;
  recipientsOnly?: boolean;
  org: Org;
  triggerType: StartDateTriggeredCommunicationTriggerType;
  onClose: () => void;
  interval?: EmployeeAutomationSendInterval;
  copiedFromAutomationId?: number;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

const parseHourAndMinute = (timeString: string) => {
  const parsedHour = parseInt(timeString.split(':')[0], 10);
  const hour = parsedHour > 12 ? parsedHour - 12 : parsedHour;
  const minute = parseInt(timeString.split(':')[1], 10);
  const meridiem = parsedHour < 12 ? 'AM' : 'PM';

  return { hour, minute, meridiem };
};

const getMissingMessageFields = (messageBody?: string, subject?: string) => {
  const missing: string[] = [];
  if (!messageBody) {
    missing.push('message');
  }
  if (!subject) {
    missing.push('subject');
  }
  return missing;
};

export const EmployeeAutomationDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    onClose,
    employees,
    employeesStateForOrgIdAndStatus,
    actions,
    surveys,
    org,
    startDateTriggeredCommunications,
    users,
    editingCommunication,
    recipientsOnly,
    triggerType,
    interval,
    copiedFromAutomationId,
  }: Props) => {
    const orgId = org.org_id;

    useEffect(() => {
      actions.loadOrgUsers();
      actions.loadSurveys();
      actions.loadEmployees();
      actions.loadStartDateTriggeredCommunications();
    });

    const theme = useTheme();
    const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

    const currentTriggerType = editingCommunication?.triggerType || triggerType;

    if (!currentTriggerType) {
      throw new Error('Must specify a triggerType or be editing an existing communication');
    }

    // Use absolute value to always display a positive integer
    const defaultSendInterval = editingCommunication
      ? Math.abs(editingCommunication.sendInterval)
      : 1;
    const defaultSendIntervalType =
      editingCommunication?.sendIntervalType ||
      (currentTriggerType === AUTOMATION_TRIGGER_TYPES.START_DATE ? 'DAY' : 'YEAR');

    const defaultSendAnnually = editingCommunication
      ? sendingAnnually(editingCommunication) && !offsetAndRecurrenceSame(editingCommunication)
      : false;
    const defaultSelectedOffset = editingCommunication
      ? sendOffsetType(editingCommunication)
      : currentTriggerType === AUTOMATION_TRIGGER_TYPES.START_DATE
      ? 'After'
      : 'Every';
    const defaultDescription =
      editingCommunication?.description ||
      (currentTriggerType === AUTOMATION_TRIGGER_TYPES.START_DATE
        ? '1 Day Message'
        : 'Birthday Message');

    const editingSpanishMessageBody =
      editingCommunication?.StartDateTriggeredMessage?.spanishMessageBody;
    const editingSpanishSubject = editingCommunication?.StartDateTriggeredMessage?.spanishSubject;
    const defaultLanguageToMessage = {
      en: editingCommunication?.StartDateTriggeredMessage?.messageBody ?? '',
      ...(editingSpanishMessageBody ? { es: editingSpanishMessageBody } : {}),
    };
    const defaultLanguageToSubject = {
      en: editingCommunication?.StartDateTriggeredMessage?.subject ?? '',
      ...(editingSpanishSubject ? { es: editingSpanishSubject } : {}),
    };

    const {
      hour: defaultHour,
      minute: defaultMinute,
      meridiem: defaultMeridiem,
    } = parseHourAndMinute(editingCommunication?.sendAt || DEFAULT_SDTC_SEND_TIME);
    const defaultTimezone = editingCommunication?.timeZoneId || getTimeZoneFromOffset();

    const [errors, setErrors] = useState<Array<string>>([]);
    const [communicationType, setCommunicationType] = useState<StartDateTriggeredCommunicationType>(
      editingCommunication?.communicationType ?? START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE,
    );
    const [sendIntervalType, setSendIntervalType] =
      useState<StartDateTriggeredCommunicationIntervalType>(
        interval?.sendIntervalType || defaultSendIntervalType,
      );
    const [selectedOffset, setSelectedOffset] = useState<StartDateTriggeredCommunicationOffsetType>(
      interval?.sendOffset || defaultSelectedOffset,
    );
    const [sendAnnually, setSendAnnually] = useState<boolean>(
      Boolean(interval?.sendAnnually || defaultSendAnnually),
    );
    const [sendInterval, setSendInterval] = useState<number | ''>(
      interval?.sendInterval || defaultSendInterval,
    );
    const [selectedHour, setSelectedHour] = useState<number>(defaultHour);
    const [selectedMinute, setSelectedMinute] = useState<number>(defaultMinute);
    const [selectedMeridiemIndicator, setSelectedMeridiemIndicator] =
      useState<string>(defaultMeridiem);
    const [timeZoneId, setTimeZoneId] = useState<string>(defaultTimezone);

    const [surveyId, setSurveyId] = useState<string>(
      editingCommunication?.surveyId?.toString() ?? '',
    );
    // Default needs to be set to anything that matches the regex for Birthdate or StartDate, it'll get set correctly on load
    const [description, setDescription] = useState<string>(defaultDescription);
    const [hasVCardAttached, setHasVCardAttached] = useState<boolean>(
      editingCommunication?.StartDateTriggeredMessage?.hasVCardAttached ?? false,
    );
    const [displayVCardDialog, setDisplayVCardDialog] = useState<boolean>(false);

    const [alertLanguageToMessage, setAlertLanguageToMessage] = useState<
      Record<LanguageCode, string>
    >({
      en: editingCommunication?.StartDateTriggeredAlert?.messageBody ?? '',
      ...(editingCommunication?.StartDateTriggeredAlert?.spanishMessageBody
        ? { es: editingCommunication?.StartDateTriggeredAlert?.spanishMessageBody }
        : {}),
    });
    const [alertLanguageToSubject, setAlertLanguageToSubject] = useState<
      Record<LanguageCode, string>
    >({
      en: editingCommunication?.StartDateTriggeredAlert?.subject ?? '',
      ...(editingCommunication?.StartDateTriggeredAlert?.spanishSubject
        ? {
            es: editingCommunication?.StartDateTriggeredAlert?.subject ?? '',
          }
        : {}),
    });
    const [sendToSupervisors, setSendToSupervisors] = useState<boolean>(
      Boolean(editingCommunication?.StartDateTriggeredAlert?.sendToSupervisors),
    );
    const [startDateTriggeredAlertRecipients, setStartDateTriggeredAlertRecipients] = useState<
      Array<{ username: string }>
    >(editingCommunication?.StartDateTriggeredAlert?.StartDateTriggeredAlertRecipients ?? []);

    const [messageLanguageToMessage, setMessageLanguageToMessage] =
      useState<Record<LanguageCode, string>>(defaultLanguageToMessage);
    const [messageLanguageToSubject, setMessageLanguageToSubject] =
      useState<Record<LanguageCode, string>>(defaultLanguageToSubject);
    const [messageType, setMessageType] = useState<MessageType>(
      editingCommunication?.StartDateTriggeredMessage?.messageType ?? MESSAGE_TYPE.TEXT,
    );

    const [isCreatingNewSurvey, setIsCreatingNewSurvey] = useState<boolean>(false);
    const [displayAdvancedSettings, setDisplayAdvancedSettings] = useState<boolean>(false);

    const [recipientType, setRecipientType] = useState<string>(
      editingCommunication?.EmployeeAutomationRecipients?.length ? 'GROUP' : 'ALL',
    );
    const [recipients, setRecipients] = useState<Array<EmployeeAutomationRecipient>>(
      editingCommunication?.EmployeeAutomationRecipients || [],
    );
    const selectedGroupIds = recipients.map((r) => r.employeeGroupId);

    useEffect(() => {
      const regexDefaultDescriptionMatch =
        /^((Every|Annual)?\s?\d*\s?(Day|Days|Week|Weeks|Month|Months|Year|Years)\s?(After|Before)?\s?)?\s?(Message|Employment Reminder|Survey|Onboarding Survey|Birthday Message|Birthday Notification to Supervisor|Birthday Survey)$/;
      // If a custom description is being used when a value is changed, return the description as is
      if (!regexDefaultDescriptionMatch.test(description)) {
        setDescription(description);
      } else if (
        triggerType === AUTOMATION_TRIGGER_TYPES.BIRTHDATE &&
        selectedOffset === 'Every' &&
        sendInterval === 1 &&
        sendIntervalType === 'YEAR'
      ) {
        // If we have a Birthdate automation with default settings, use simplified description
        setDescription(
          `Birthday${
            communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT
              ? ' Notification to Supervisor'
              : ` ${capitalize(communicationType?.toLowerCase() || '')}`
          }`,
        );
      } else {
        // Otherwise, StartDate and Birthdate descriptions will have the same prefix text
        let desc = '';
        if (selectedOffset === 'Every') {
          desc += `Every ${
            sendInterval && sendInterval !== 1 ? `${sendInterval.toString()} ` : ''
          }`;
          desc += `${capitalize(sendIntervalType?.toLowerCase() || '')}${
            sendInterval && sendInterval !== 1 ? 's' : ''
          }`;
        } else {
          // After or Before are selected
          desc += `${sendAnnually ? 'Annual ' : ''}${
            sendInterval ? `${sendInterval.toString()} ` : ''
          }`;
          desc += `${capitalize(sendIntervalType?.toLowerCase() || '')}${
            sendInterval && sendInterval !== 1 ? 's' : ''
          } ${selectedOffset}`;
        }
        // Set the default based on communication type
        if (triggerType === AUTOMATION_TRIGGER_TYPES.BIRTHDATE) {
          setDescription(
            `${desc} Birthday${
              communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT
                ? ' Notification to Supervisor'
                : ` ${capitalize(communicationType?.toLowerCase() || '')}`
            }`,
          );
        } else if (communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE) {
          setDescription(`${desc} Message`);
        } else if (communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT) {
          setDescription(`${desc} Employment Reminder`);
        } else if (
          surveys?.find((s) => s.surveyId === Number(surveyId))?.surveyType === 'onboarding'
        ) {
          setDescription(`${desc} Onboarding Survey`);
        } else {
          setDescription(`${desc} Survey`);
        }
      }
    }, [
      description,
      triggerType,
      communicationType,
      surveys,
      sendInterval,
      sendIntervalType,
      surveyId,
      selectedOffset,
      sendAnnually,
    ]);

    const handleVCardCheckboxChange = (
      _event: React.ChangeEvent<HTMLInputElement>,
      checked: boolean,
    ) => setHasVCardAttached(checked);
    const handleDisplayVCardDialog = () => setDisplayVCardDialog(!displayVCardDialog);
    const handleToggleCreateSurvey = () => setIsCreatingNewSurvey(!isCreatingNewSurvey);
    const handleOnCreateSurvey = (newSurveyId: number) => {
      setSurveyId(String(newSurveyId));
    };
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setDescription(e.target.value);
    };
    const handleSendIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setErrors([]);
      const newSendInterval = e.target.value === '' ? '' : parseInt(e.target.value, 10);
      if (!Number.isNaN(newSendInterval)) {
        setSendInterval(newSendInterval);
      }
    };
    const handleCommunicationTypeChange = (value: string) => {
      setErrors([]);
      // set the default sendAt to be more useful to our users:
      // For alerts - use 8:00am - emails can be more helpful to have top of day
      // For surveys or messages (employee facing texts) - use 12:00pm - send during the workday
      if (
        value === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT &&
        selectedHour === 12 &&
        selectedMinute === 0 &&
        selectedMeridiemIndicator === 'PM'
      ) {
        // if the new automation is changed to be an alert and the sendAt is still the default (12:00:00), change it to 08:00:00 - don't override anything existing
        const { hour, minute, meridiem } = parseHourAndMinute(
          editingCommunication?.sendAt || DEFAULT_SDTA_SEND_TIME,
        );
        setSelectedHour(hour);
        setSelectedMinute(minute);
        setSelectedMeridiemIndicator(meridiem);
      } else if (
        value !== START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT &&
        selectedHour === 8 &&
        selectedMinute === 0 &&
        selectedMeridiemIndicator === 'AM'
      ) {
        // if the new automation is changed to be a message or survey and the sendAt is the alert default, change it to 12:00:00 - don't override anything existing
        const { hour, minute, meridiem } = parseHourAndMinute(
          editingCommunication?.sendAt || DEFAULT_SDTC_SEND_TIME,
        );
        setSelectedHour(hour);
        setSelectedMinute(minute);
        setSelectedMeridiemIndicator(meridiem);
      }
      setCommunicationType(value as StartDateTriggeredCommunicationType);
    };
    const handleSendIntervalTypeChange = (value: string) => {
      setErrors([]);
      setSendIntervalType(value as StartDateTriggeredCommunicationIntervalType);
    };
    const handleSelectedOffsetChange = (value: string) => {
      // If we are changing from Every to After or Before for Birthdate, default to sending annually
      if (
        triggerType === AUTOMATION_TRIGGER_TYPES.BIRTHDATE &&
        value !== 'Every' &&
        selectedOffset === 'Every'
      ) {
        setSendAnnually(true);
      }
      setErrors([]);
      setSelectedOffset(value as StartDateTriggeredCommunicationOffsetType);
    };
    const handleSurveyIdChange = (
      _e: React.ChangeEvent<{}>,
      option: SelectOption<string> | null,
    ) => {
      if (option) {
        setErrors([]);
        setSurveyId(option.value);
      }
    };
    const handleMessageChange = (newLanguageToMessage: Record<LanguageCode, string>) => {
      setErrors([]);
      setMessageLanguageToMessage(newLanguageToMessage);
    };
    const handleSubjectChange = (subject: Record<LanguageCode, string>) => {
      setErrors([]);
      setMessageLanguageToSubject(subject);
    };
    const handleMessageTypeChange = (type: MessageType) => () => {
      setErrors([]);
      setMessageType(type);
    };
    const defaultComposerHandleMessageTypeChange = (type: MessageType) => {
      handleMessageTypeChange(type)();
    };
    const handleAlertMessageBodyChange = (
      newAlertLanguageToMessage: Record<LanguageCode, string>,
    ) => {
      setErrors([]);
      setAlertLanguageToMessage(newAlertLanguageToMessage);
    };
    const handleAlertSubjectChange = (newAlertLanguageToSubject: Record<LanguageCode, string>) => {
      setErrors([]);
      setAlertLanguageToSubject(newAlertLanguageToSubject);
    };
    const handleSendToSupervisorChange = (
      _e: React.ChangeEvent<HTMLInputElement>,
      checked: boolean,
    ) => {
      setErrors([]);
      setSendToSupervisors(checked);
    };
    const handleSetRecipientType = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
      if (value === 'ALL') {
        setRecipients([]);
      }
      setRecipientType(value);
    };
    const handleAddGroupRecipient = (addId: number) => {
      const newRecipients = [...recipients, { employeeGroupId: addId }];
      setRecipients(newRecipients);
    };
    const handleRemoveGroupRecipient = (removeId: number) => {
      const newRecipients = recipients.filter((r) => r.employeeGroupId !== removeId);
      setRecipients(newRecipients);
    };
    const handleRecipientChange = (usernames: Array<{ username: string }>) => {
      setStartDateTriggeredAlertRecipients(usernames);
    };
    const handleSendAnnuallyClick = () => {
      setSendAnnually(!sendAnnually);
    };

    // Changes the hour into the format that we want to store in the database (a value 0-23)
    // With 0 being midnight, and 12 being noon
    // if the selected hour is 12 AM, set to 0 as midnight
    // if the selected out is 12 PM, keep as 12 noon
    // if we have any other value in the afternoon, add 12 to it to differentiate 1 AM (1) from 1 PM (13)
    const getSendAtHour = () => {
      let hour = selectedHour;
      if (hour === 12 && selectedMeridiemIndicator === 'AM') {
        hour = 0;
      } else if (hour === 12 && selectedMeridiemIndicator === 'PM') {
        hour = 12;
      } else if (selectedMeridiemIndicator === 'PM') {
        hour += 12;
      }

      const formattedHour = `${hour < 10 ? '0' : ''}${hour}`;

      return formattedHour;
    };
    const handleHourChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      setSelectedHour(e.target.value as number);
    };
    const handleMinuteChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      setSelectedMinute(e.target.value as number);
    };
    const handleMeridiemChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      setSelectedMeridiemIndicator(e.target.value as string);
    };
    const handleTimeZoneIdChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      setTimeZoneId(e.target.value as string);
    };

    const handleToggleDisplayAdvancedSettings = () => {
      setDisplayAdvancedSettings(!displayAdvancedSettings);
    };

    const dialogTitle = recipientsOnly
      ? 'Edit Recipients'
      : `${editingCommunication?.startDateTriggeredCommunicationId ? 'Edit' : 'Add'} ${
          triggerType === AUTOMATION_TRIGGER_TYPES.START_DATE ? 'Start Date' : 'Birthday'
        } Automation`;

    if (
      !employees ||
      !employeesStateForOrgIdAndStatus ||
      !employeesStateForOrgIdAndStatus.success ||
      !surveys ||
      !startDateTriggeredCommunications ||
      !users
    ) {
      return (
        <UiDialog title={dialogTitle} onClose={onClose}>
          <UiDialog.Content>
            <LoadingMessage />
          </UiDialog.Content>
        </UiDialog>
      );
    }

    const handleSubmit = () => {
      const issues = [];

      if (!sendInterval && sendInterval !== 0) {
        issues.push('Please select a number of days');
      }

      if (!description.trim()) {
        issues.push('Please add a title');
      }

      const requestParams: any = {
        description,
        communicationType,
        sendInterval,
        sendIntervalType,
        isRecurring: selectedOffset === 'Every' || sendAnnually || false,
        triggerType,
        orgId,
        sendAt: `${getSendAtHour()}:${selectedMinute < 10 ? '0' : ''}${selectedMinute}:00`,
        timeZoneId,
        copiedFromAutomationId,
      };

      if (selectedOffset === 'Before') {
        requestParams.sendInterval = Math.abs(requestParams.sendInterval) * -1;
      }

      if (selectedOffset === 'Every') {
        requestParams.recurrenceInterval = requestParams.sendInterval;
        requestParams.recurrenceIntervalType = requestParams.sendIntervalType;
      } else if (sendAnnually) {
        requestParams.recurrenceInterval = 1;
        requestParams.recurrenceIntervalType = 'YEAR';
      }

      const recipientsSame =
        selectedGroupIds.sort().toString() ===
        (editingCommunication?.EmployeeAutomationRecipients || [])
          .map((r) => r.employeeGroupId)
          .sort()
          .toString();
      if (!recipientsSame || !editingCommunication?.startDateTriggeredCommunicationId) {
        requestParams.EmployeeAutomationRecipients = recipients;
      }

      if (communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE) {
        // This code is copy-pasted from `multiple-language-message-composer`
        // Most places we use it, we just pass in an `onSendMessage` prop, but in this component
        // we manage the `languageToMessage` sending outside the component. That means that
        // this component is then responsible for doing this work, which is done in the `onSendMessage`
        // method
        const simpleLanguageToMessage = CkEditorNewlineTrimmer.getSimpleTrimmedHash(
          messageLanguageToMessage,
          messageType,
        );
        const { en: englishMessageBody, es: spanishMessageBody } = simpleLanguageToMessage;
        const { en: englishSubject, es: spanishSubject } = messageLanguageToSubject;
        if (!englishMessageBody && !spanishMessageBody) {
          issues.push('Please add a message');
        }

        const isEmail = messageType === MESSAGE_TYPE.EMAIL;
        requestParams.StartDateTriggeredMessage = {
          messageType,
          messageBody: englishMessageBody || spanishMessageBody,
          spanishMessageBody: englishMessageBody && spanishMessageBody ? spanishMessageBody : null,
          subject: isEmail ? englishSubject || spanishSubject : null,
          spanishSubject: isEmail && englishSubject && spanishSubject ? spanishSubject : null,
          hasVCardAttached,
        };
      } else if (communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.SURVEY) {
        if (!surveyId) {
          issues.push('Please select a survey');
        }

        const automationAlreadyExists = startDateTriggeredCommunications.find(
          (c) =>
            c.startDateTriggeredCommunicationId !==
              editingCommunication?.startDateTriggeredCommunicationId &&
            c.orgId === orgId &&
            c.surveyId === parseInt(surveyId, 10) &&
            c.sendInterval === sendInterval &&
            c.EmployeeAutomationRecipients.every((g) =>
              selectedGroupIds.includes(g.employeeGroupId),
            ) &&
            c.EmployeeAutomationRecipients.length === selectedGroupIds.length &&
            !c.deletedAt,
        );

        if (automationAlreadyExists) {
          issues.push('That automation already exists');
        }

        requestParams.surveyId = parseInt(surveyId, 10);
      } else if (communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT) {
        const missingEnglishFields = getMissingMessageFields(
          alertLanguageToMessage.en,
          alertLanguageToSubject.en,
        );
        missingEnglishFields.forEach((field) => {
          issues.push(`Please add a ${field} for the alert`);
        });

        // only add Spanish issues if English fields populated
        if (missingEnglishFields.length === 0) {
          const missingSpanishMessageField = getMissingMessageFields(
            alertLanguageToMessage.es,
            alertLanguageToSubject.es,
          );
          if (missingSpanishMessageField.length === 1) {
            issues.push(
              `Please add a ${missingSpanishMessageField[0]} for the ${LANGUAGES.es} alert`,
            );
          }
        }

        if (startDateTriggeredAlertRecipients.length === 0 && !sendToSupervisors) {
          issues.push('Please select at least one person to receive this alert');
        }

        // IF (03/04/21): We show recipientsOnly when someone is trying to turn on an alert that has
        // no recipients. If that's the case, we want to turn this on once they're set.
        if (recipientsOnly) {
          requestParams.isOn = true;
        }

        requestParams.StartDateTriggeredAlert = {
          messageBody: alertLanguageToMessage.en,
          subject: alertLanguageToSubject.en,
          spanishMessageBody: alertLanguageToMessage.es || null,
          spanishSubject: alertLanguageToSubject.es || null,
          sendToSupervisors,
          StartDateTriggeredAlertRecipients: startDateTriggeredAlertRecipients,
        };
      }

      if (issues.length) {
        setErrors(issues);
        return;
      }

      // IF (09/11/20): Typescript is struggling to realize that sendInterval can't be empty here. If
      // that is the case, we push a string onto the issues array and would return early
      if (editingCommunication && editingCommunication?.startDateTriggeredCommunicationId) {
        actions.updateStartDateTriggeredCommunication(
          editingCommunication.startDateTriggeredCommunicationId,
          requestParams as StartDateTriggeredCommunicationUpdateRequest,
        );
      } else {
        actions.createStartDateTriggeredCommunication(
          requestParams as StartDateTriggeredCommunicationCreateRequest,
        );
      }
      onClose();
    };

    const handleAddLanguage = (language: LanguageCode) => () => {
      if (communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE) {
        setMessageLanguageToMessage({
          ...messageLanguageToMessage,
          [language]: messageLanguageToMessage[language] || '',
        });
        setMessageLanguageToSubject({
          ...messageLanguageToSubject,
          [language]: messageLanguageToSubject[language] || '',
        });
      } else if (communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT) {
        setAlertLanguageToMessage({
          ...alertLanguageToMessage,
          [language]: alertLanguageToMessage[language] || '',
        });
        setAlertLanguageToSubject({
          ...alertLanguageToSubject,
          [language]: alertLanguageToSubject[language] || '',
        });
      }
    };

    const hourOptions = Array.from(Array(12), (_, i) => {
      const hour = i + 1;
      return { value: hour, label: hour.toString() };
    });
    const minuteOptions = Array.from(Array(4), (_, i) => {
      const minute = i * 15;
      return { value: minute, label: minute.toString().padStart(2, '0') };
    });

    const currentEmployees = employees.filter((e) => e.isCurrentEmployee);
    const languagesToSupport =
      communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE
        ? Object.keys(messageLanguageToMessage)
        : communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT
        ? Object.keys(alertLanguageToMessage)
        : undefined;

    const communicationTypeOptions = [
      {
        value: START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE,
        label: 'Send Message',
      },
      {
        value: START_DATE_TRIGGERED_COMMUNICATION_TYPES.SURVEY,
        label: 'Send Survey',
      },
      {
        value: START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT,
        label: 'Receive Notification',
      },
    ];
    const surveyOptions = surveys.map((s) => ({
      value: s.surveyId.toString(),
      label: `${s.messageBody}`,
    }));
    const selectedSurvey = surveyOptions.find((o) => o.value === surveyId) ?? null;
    const sendIntervalTypeOptions = ['Day', 'Week', 'Month', 'Year'].map((o) => ({
      value: o.toUpperCase(),
      label: `${o}${sendInterval === 1 ? '' : 's'}`,
    }));
    const recurringOptions = ['After', 'Before', 'Every'].map((o) => ({
      value: o,
      label: o,
    }));

    const missingSupervisors = issueFilters.missingSupervisor.filter(currentEmployees);
    const link = issueFilters.missingSupervisor.link(orgId);

    const shouldRenderAddLanguageButton =
      (communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE &&
        !('es' in messageLanguageToMessage)) ||
      (communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT &&
        !('es' in alertLanguageToMessage));

    const renderRecipientSelector = () => (
      <>
        {!editingCommunication?.startDateTriggeredCommunicationId &&
          sendToSupervisors &&
          missingSupervisors.length > 0 && (
            <div className="u-margin-bottom-lg">
              <Message variant="error" canBeDismissed={false}>
                <div className="u-spread">
                  <Link to={link}>
                    Some employees are missing <span className="typo-bold"> supervisors</span> and
                    they will not receive notifications.
                  </Link>
                  <UiButton size="xs" linkTo={link}>
                    View Issues
                  </UiButton>
                </div>
              </Message>
            </div>
          )}
        <MultipleEmployeeUsersSelector
          label="Who should receive this alert"
          orgId={orgId}
          value={startDateTriggeredAlertRecipients}
          onChange={handleRecipientChange}
          className="u-margin-top"
        >
          <div
            className="u-display-flex u-alignItems-center u-padding-sm"
            style={{ borderTop: '1px solid #c4c4c4' }}
          >
            <Checkbox
              className="u-no-padding"
              checked={sendToSupervisors}
              onChange={handleSendToSupervisorChange}
            />
            <div className="u-margin-left-sm">Send to employee&apos;s supervisor(s)</div>
          </div>
        </MultipleEmployeeUsersSelector>
      </>
    );

    const renderErrors = () =>
      errors.length ? (
        <Message className="u-margin-top" variant="error" canBeDismissed={false}>
          {errors.map((e) => (
            <div key={e}>{e}</div>
          ))}
        </Message>
      ) : null;

    if (recipientsOnly) {
      return (
        <UiDialog title={dialogTitle} onClose={onClose} size="sm">
          <UiDialog.Content>
            {renderRecipientSelector()}
            {renderErrors()}
          </UiDialog.Content>
          <UiDialog.Actions>
            <UiButton onClick={handleSubmit}>Update</UiButton>
          </UiDialog.Actions>
        </UiDialog>
      );
    }

    return (
      <UiDialog title={dialogTitle} onClose={onClose} size="sm">
        <UiDialog.Content>
          <div className="u-display-flex u-alignItems-top">
            <div className="u-width-100">
              <UiSelect
                className="u-width-100 u-no-margin"
                options={communicationTypeOptions}
                value={communicationType}
                onChange={handleCommunicationTypeChange}
                dataCy="automation-type-select"
              />
            </div>
            <div className="u-margin-left-sm">
              <UiSelect
                className="u-no-margin"
                options={recurringOptions}
                value={selectedOffset}
                onChange={handleSelectedOffsetChange}
              />
            </div>
            <div className="u-margin-left-sm">
              <TextField
                className="u-no-margin"
                variant="outlined"
                margin="dense"
                value={sendInterval}
                onChange={handleSendIntervalChange}
                inputProps={{ type: 'number' }}
              />
            </div>
            <div className="u-margin-left-sm">
              <UiSelect
                className="u-no-margin"
                options={sendIntervalTypeOptions}
                value={sendIntervalType}
                onChange={handleSendIntervalTypeChange}
              />
            </div>
          </div>
          <div className="u-margin-top u-margin-bottom card">
            <div className="u-margin-left u-margin-right">
              <RadioGroup value={recipientType} onChange={handleSetRecipientType}>
                <div className="u-display-flex">
                  <FormLabel className="u-alignSelf-center u-margin-right">
                    {communicationType === 'alert' ? 'Notify for: ' : 'Send To: '}
                  </FormLabel>
                  <FormControlLabel value="ALL" control={<Radio />} label="All Employees" />
                  <FormControlLabel
                    value="GROUP"
                    control={<Radio />}
                    label="Selected Employee Groups"
                  />
                </div>
              </RadioGroup>
              {recipientType === 'GROUP' ? (
                <div className="u-margin-bottom">
                  <EmployeeGroupsBase
                    addButtonLabel="Add Group"
                    onAdd={handleAddGroupRecipient}
                    onRemove={handleRemoveGroupRecipient}
                    employeeGroupIds={selectedGroupIds}
                    orgId={org.org_id}
                  />
                </div>
              ) : null}
            </div>
          </div>
          <UiHideShowToggle
            onChange={handleToggleDisplayAdvancedSettings}
            title="additional settings"
            isOpen={displayAdvancedSettings}
            className="u-margin-bottom-sm"
          />
          {displayAdvancedSettings ? (
            <div className="u-padding u-margin-bottom u-width-100 card">
              <div className="u-display-flex u-alignItems-flexEnd u-margin-bottom-lg">
                <div className="typo-title">{`Send ${communicationType} at: `}</div>
                <div className="u-margin-x u-spread u-width-150px">
                  <Select
                    className="u-no-margin"
                    value={selectedHour}
                    onChange={handleHourChange}
                    variant="standard"
                  >
                    {hourOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    className="u-no-margin"
                    value={selectedMinute}
                    onChange={handleMinuteChange}
                    variant="standard"
                  >
                    {minuteOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    className="u-no-margin"
                    value={selectedMeridiemIndicator}
                    onChange={handleMeridiemChange}
                    variant="standard"
                  >
                    {[
                      { value: 'AM', label: 'AM' },
                      { value: 'PM', label: 'PM' },
                    ].map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className="u-margin-left-sm">
                  <Select
                    className="u-no-margin"
                    value={timeZoneId}
                    onChange={handleTimeZoneIdChange}
                    variant="standard"
                  >
                    {SDTC_TIME_ZONE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              {selectedOffset !== 'Every' && (
                <div className="u-display-flex u-alignItems-center">
                  <Checkbox
                    className="u-no-padding"
                    checked={sendAnnually}
                    onChange={handleSendAnnuallyClick}
                  />
                  <div className="typo-title u-margin-left">Send Annually</div>
                </div>
              )}
              {communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE && (
                <div
                  className={`${
                    selectedOffset !== 'Every' ? 'u-margin-top' : ''
                  } u-display-flex u-alignItems-center`}
                >
                  <Checkbox
                    className="u-no-padding"
                    checked={hasVCardAttached}
                    onChange={handleVCardCheckboxChange}
                  />
                  <div className="typo-title u-margin-left">
                    Send with contact card
                    {org.phoneNumber && (
                      <>
                        <span
                          role="presentation"
                          onClick={handleDisplayVCardDialog}
                          className="typo-subtitle typo-italic typo-link u-margin-left-sm"
                        >
                          View my contact card
                        </span>
                        {displayVCardDialog && (
                          <VCardDialog
                            handleToggleDialog={handleDisplayVCardDialog}
                            name={org.name}
                            phone={org.phoneNumber}
                            logo_path={org.logo_path}
                            org_key={org.org_key}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          <TextField
            className="u-width-100 u-margin-top"
            variant="outlined"
            margin="dense"
            label="Title"
            value={description}
            onChange={handleDescriptionChange}
            required
          />
          {communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT &&
            renderRecipientSelector()}
          {communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE && (
            <div className="u-margin-top">
              <FeatureFlagged
                variant={FeatureGatedVariant.HIDE_ON_FLAG}
                flagName={FeatureGates.INTEGRATED_MESSAGING}
              >
                <LegacyMultiLanguageMessageComposer
                  languageToMessage={messageLanguageToMessage}
                  languageToSubject={messageLanguageToSubject}
                  languagesToSupport={languagesToSupport}
                  enableText
                  enableEmail
                  initialType={messageType}
                  showMessageTypeSwitcher={false}
                  messageType={messageType}
                  autofocus
                  canPersonalize
                  canTranslate
                  disableAddLanguage
                  onChange={handleMessageChange}
                  // Here, we want the button to be in line with
                  // the update button, so we disable the composer's built in switcher
                  onMessageTypeChange={defaultComposerHandleMessageTypeChange}
                  onSubjectChange={handleSubjectChange}
                />
              </FeatureFlagged>
              <FeatureFlagged
                variant={FeatureGatedVariant.SHOW_ON_FLAG}
                flagName={FeatureGates.INTEGRATED_MESSAGING}
              >
                <MultiLanguageMessageComposer
                  languageToMessage={messageLanguageToMessage}
                  languageToSubject={messageLanguageToSubject}
                  languagesToSupport={languagesToSupport}
                  enableText
                  enableEmail
                  initialType={messageType}
                  showMessageTypeSwitcher={false}
                  messageType={messageType}
                  autofocus
                  canPersonalize
                  canTranslate
                  disableAddLanguage
                  onChange={handleMessageChange}
                  // Here, we want the button to be in line with
                  // the update button, so we disable the composer's built in switcher
                  onMessageTypeChange={defaultComposerHandleMessageTypeChange}
                  onSubjectChange={handleSubjectChange}
                  isOnMobile={isOnMobile}
                />
              </FeatureFlagged>
            </div>
          )}
          {communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.SURVEY && (
            <>
              <Autocomplete
                className="u-width-100"
                options={surveyOptions}
                onChange={handleSurveyIdChange}
                getOptionLabel={(o) => o.label}
                value={selectedSurvey}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select existing survey"
                    margin="dense"
                    variant="outlined"
                  />
                )}
              />
              <div
                role="presentation"
                onClick={handleToggleCreateSurvey}
                className="typo-subtitle typo-link"
              >
                Or, create a new survey
              </div>
              {isCreatingNewSurvey ? (
                <CreateSurveyDialog
                  orgId={orgId}
                  onClose={handleToggleCreateSurvey}
                  onCreate={handleOnCreateSurvey}
                  surveyType="standard"
                  createWithNoRecipients
                />
              ) : null}
            </>
          )}
          {communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT && (
            <div className="u-margin-top">
              <FeatureFlagged
                variant={FeatureGatedVariant.HIDE_ON_FLAG}
                flagName={FeatureGates.INTEGRATED_MESSAGING}
              >
                <LegacyMultiLanguageMessageComposer
                  languageToMessage={alertLanguageToMessage}
                  languageToSubject={alertLanguageToSubject}
                  languagesToSupport={languagesToSupport}
                  enableEmail
                  enableText={false}
                  canPersonalize
                  canTranslate
                  disableAddLanguage
                  onChange={handleAlertMessageBodyChange}
                  onSubjectChange={handleAlertSubjectChange}
                />
              </FeatureFlagged>
              <FeatureFlagged
                variant={FeatureGatedVariant.SHOW_ON_FLAG}
                flagName={FeatureGates.INTEGRATED_MESSAGING}
              >
                <MultiLanguageMessageComposer
                  languageToMessage={alertLanguageToMessage}
                  languageToSubject={alertLanguageToSubject}
                  languagesToSupport={languagesToSupport}
                  enableEmail
                  enableText={false}
                  canPersonalize
                  canTranslate
                  disableAddLanguage
                  onChange={handleAlertMessageBodyChange}
                  onSubjectChange={handleAlertSubjectChange}
                  isOnMobile={isOnMobile}
                />
              </FeatureFlagged>
            </div>
          )}
          {renderErrors()}
        </UiDialog.Content>
        <UiDialog.Actions>
          <div className="u-width-100 u-display-flex u-gap-10px u-justifyContent-spaceBetween">
            {communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE ? (
              <>
                <FeatureFlagged
                  variant={FeatureGatedVariant.HIDE_ON_FLAG}
                  flagName={FeatureGates.INTEGRATED_MESSAGING}
                >
                  <UiButton
                    variant="tertiary"
                    onClick={handleMessageTypeChange(
                      messageType === MESSAGE_TYPE.TEXT ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT,
                    )}
                    dataCy="employee-automation-type-switcher"
                  >
                    Switch to{' '}
                    {messageType === MESSAGE_TYPE.TEXT ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT}
                  </UiButton>
                </FeatureFlagged>
                <FeatureFlagged
                  variant={FeatureGatedVariant.SHOW_ON_FLAG}
                  flagName={FeatureGates.INTEGRATED_MESSAGING}
                >
                  <UiSliderSelect
                    options={[
                      {
                        value: MESSAGE_TYPE.TEXT,
                        label: humanize(MESSAGE_TYPE.TEXT),
                      },
                      {
                        value: MESSAGE_TYPE.EMAIL,
                        label: humanize(MESSAGE_TYPE.EMAIL),
                      },
                    ]}
                    selectedValue={messageType}
                    onChange={handleMessageTypeChange(
                      messageType === MESSAGE_TYPE.TEXT ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT,
                    )}
                    dataCy="employee-automation-type-switcher"
                  />
                </FeatureFlagged>
              </>
            ) : null}
            {shouldRenderAddLanguageButton && (
              <UiButton onClick={handleAddLanguage('es')} variant="tertiary">
                Add Spanish version
              </UiButton>
            )}
            <UiButton onClick={handleSubmit} className="u-margin-left-auto">
              {editingCommunication?.startDateTriggeredCommunicationId ? 'Update' : 'Add'}
            </UiButton>
          </div>
        </UiDialog.Actions>
      </UiDialog>
    );
  },
);
